import { useEffect, useState } from 'react';
import { F, Btn } from '../../lib/bricks';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { List, Row, Empty, Section, FieldLabel, TextLink } from '../calm/Calm';
import { STATE_TEXT, connectionMeta, rowMeta, fmtWhen, mappingMissing } from './books';
import { visibleBooks } from '../../lib/integrations';

const fmt = (v) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(v || 0);

export default function BooksSync({ data: raw, p, dark, mapOpen, setMapOpen, call }) {
  const [opts, setOpts] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState('');
  const data = visibleBooks(raw);
  const conn = data?.connection;
  const roles = data?.roles || [];

  useEffect(() => {
    if (!mapOpen) return;
    setOpts(null);
    call('GET', 'accounts').then((r) => { if (r) { setOpts(r.options); setSel(r.selected || {}); } else setMapOpen(false); });
  }, [mapOpen]); // eslint-disable-line

  const run = async (key, fn) => { setBusy(key); try { await fn(); } finally { setBusy(''); } };
  const selectStyle = { marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' };
  const missing = mappingMissing(sel, roles);

  if (!conn) {
    return (
      <Section title="Connect your books" testid="books-connect"
        note="Approved commissions post as a journal entry and an agent bill. Paid ones post the bill payment. Pending records wait.">
        <List>
          {(data?.providers || []).map((pr) => (
            <Row key={pr.key} testid={`books-provider-${pr.key}`} title={pr.name}
              meta={pr.available ? (pr.sandbox ? 'Sandbox company' : null) : "Not switched on yet"}
              aside={pr.available ? <TextLink testid={`books-connect-${pr.key}`} onClick={() => run(pr.key, () => call('POST', 'connect', { provider: pr.key }))}>{busy === pr.key ? 'Connecting…' : 'Connect'}</TextLink> : null} />
          ))}
        </List>
      </Section>
    );
  }

  return (
    <>
      <Section title="Connected books" testid="books-connection">
        <List>
          <Row title={conn.name} meta={connectionMeta(conn)}
            status={conn.status === 'needs_reconnect' ? 'Signed out' : null} attention={conn.status === 'needs_reconnect'}
            aside={<TextLink testid="books-disconnect" onClick={() => run('dc', () => call('DELETE', 'connect'))}>{busy === 'dc' ? 'Disconnecting…' : 'Disconnect'}</TextLink>} />
        </List>
      </Section>

      <Section title="Accounts" testid="books-mapping" action={conn.mapped ? <TextLink testid="books-change-mapping" onClick={() => setMapOpen(true)}>Change</TextLink> : null}
        note={conn.mapped ? null : 'Pick where commissions land before the first sync.'}>
        <List>
          {roles.map((r) => {
            const m = (conn.mapping || {})[r.key];
            return <Row key={r.key} testid={`books-role-${r.key}`} title={r.label} meta={r.help}
              status={m ? (m.code ? `${m.code} ${m.name}` : m.name) : 'Not chosen'} attention={!m} />;
          })}
        </List>
      </Section>

      <Section title="Commission records" testid="books-rows">
        {(data.rows || []).length === 0 ? <Empty>No commission records yet. They appear here as deals close.</Empty> : (
          <List>
            {data.rows.map((r) => (
              <Row key={r.id} testid={`books-row-${r.id}`} title={r.address} meta={rowMeta(r, fmt)}
                status={STATE_TEXT[r.state]} attention={r.state === 'failed'} />
            ))}
          </List>
        )}
      </Section>

      {(data.history || []).length > 0 && (
        <Section title="Recent activity" testid="books-history">
          <List>
            {data.history.slice(0, 6).map((h) => (
              <Row key={h.id} title={`${h.label}${h.number ? ` ${h.number}` : ''}`}
                meta={[h.address, fmtWhen(h.at), h.error].filter(Boolean)}
                status={h.status === 'posted' ? <span style={{ fontFamily: F.mono, fontSize: 12.5 }}>{fmt(h.amount)}</span> : "Didn't post"}
                attention={h.status !== 'posted'} />
            ))}
          </List>
        </Section>
      )}

      <Dialog open={!!mapOpen} onOpenChange={(o) => setMapOpen(o)}>
        <DialogContent style={{ maxWidth: 460, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Choose accounts</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              From your {conn.name} chart of accounts. Ask your accountant if you're not sure.
            </DialogDescription>
          </DialogHeader>
          {!opts ? <div style={{ padding: '20px 0', fontSize: 13, color: p.ink3 }}>Loading your accounts…</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 16 }}>
              {roles.map((r) => (
                <div key={r.key}>
                  <FieldLabel htmlFor={`map-${r.key}`}>{r.label}</FieldLabel>
                  <select id={`map-${r.key}`} data-testid={`map-${r.key}`} value={sel[r.key] || ''} style={selectStyle}
                    onChange={(e) => setSel((s) => ({ ...s, [r.key]: e.target.value }))}>
                    <option value="">Choose…</option>
                    {(opts[r.key] || []).map((o) => <option key={o.id} value={o.id}>{o.code ? `${o.code} ${o.name}` : o.name}{o.detail ? ` (${o.detail})` : ''}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setMapOpen(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} data-testid="map-save" disabled={!opts || missing.length > 0 || busy === 'map'}
              onClick={() => run('map', async () => { if (await call('PUT', 'mapping', sel)) setMapOpen(false); })}>
              {busy === 'map' ? 'Saving…' : 'Save accounts'}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
